import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "primereact/button";
import classNames from "classnames";

// Types
export interface ContentSection {
  id: string;
  type: "text" | "image" | "component" | "cta";
  content: {
    header?: string;
    highlightText?: string;
    infoText?: string | string[];
    imageSrc?: string;
    imageClassName?: string;
    component?: React.ReactNode;
    button?: {
      label: string;
      onClick: () => void;
      className?: string;
      outlined?: boolean;
    };
  };
  isFinalComponentInRow?: boolean;
  layout?: "normal" | "reverse";
  margin?: string;
  style?: React.CSSProperties;
  className?: string;
}

interface LandingProps {
  withInitialAnimation?: boolean;
  sections: ContentSection[];
  headerComponent?: React.ComponentType;
  styles: Record<string, string>;
  constants: {
    Y_OFFSET: number;
    DURATION: number;
    EASE: string;
    MARGIN_1: string;
    MARGIN_2: string;
  };
  requireScrollToShowComponents?: boolean;
  gap?: number;
}

// Individual section component that handles its own ref and inView
const AnimatedSection: React.FC<{
  section: ContentSection;
  styles: Record<string, string>;
  userHasScrolled: boolean;
  constants: LandingProps["constants"];
}> = ({ section, styles, userHasScrolled, constants }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, {
    once: true,
    margin: section.margin || constants.MARGIN_1,
  });

  const {
    header,
    highlightText,
    infoText,
    imageSrc,
    imageClassName,
    component,
    button,
  } = section.content;

  const renderHeader = () => {
    if (!header) return null;

    return (
      <h1 className={styles.headerText}>
        {highlightText ? (
          <>
            {header.split(highlightText)[0]}
            <span className={styles.highlightColor}>{highlightText}</span>
            {header.split(highlightText)[1]}
          </>
        ) : (
          header
        )}
      </h1>
    );
  };

  const renderInfoText = () => {
    if (!infoText) return null;

    return Array.isArray(infoText) ? (
      infoText.map((text, index) => (
        <span key={index} className={styles.infoText}>
          {text}
        </span>
      ))
    ) : (
      <span className={styles.infoText}>{infoText}</span>
    );
  };

  const renderContent = () => {
    switch (section.type) {
      case "text":
      case "cta":
        return (
          <>
            {renderHeader()}
            {renderInfoText()}
            {button && (
              <Button
                label={button.label}
                outlined={button.outlined}
                className={button.className}
                onClick={button.onClick}
              />
            )}
          </>
        );
      case "image":
        return imageSrc ? (
          <img
            src={imageSrc}
            className={imageClassName || styles.imgFadeOut}
            alt=""
          />
        ) : null;
      case "component":
        return component || null;
      default:
        return null;
    }
  };

  const getClassName = () => {
    const baseClass =
      section.type === "image" || section.type === "component"
        ? styles.contentPiece
        : styles.contentPieceText;
    return classNames(baseClass, section.className);
  };

  return (
    <motion.div
      className={getClassName()}
      ref={sectionRef}
      initial={{ opacity: 0, y: constants.Y_OFFSET }}
      animate={inView && userHasScrolled ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: constants.DURATION, ease: constants.EASE }}
      style={section.style}
    >
      {renderContent()}
    </motion.div>
  );
};

// Main Component
export const ScrollableLandingPage: React.FC<LandingProps> = ({
  withInitialAnimation,
  sections,
  headerComponent: HeaderComponent,
  styles,
  constants,
  requireScrollToShowComponents = true,
  gap = 164,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setUserHasScrolled(true);
    const scrollElement = scrollRef.current;

    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll, { once: true });
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const groupSections = (sections: ContentSection[]) => {
    const grouped: ContentSection[][] = [];
    let currentGroup: ContentSection[] = [];

    sections.forEach((section) => {
      currentGroup.push(section);
      if (section.isFinalComponentInRow) {
        grouped.push([...currentGroup]);
        currentGroup = [];
      }
    });

    if (currentGroup.length > 0) {
      grouped.push(currentGroup);
    }

    return grouped;
  };

  const groupedSections = groupSections(sections);

  return (
    <div
      className={styles.container}
      ref={scrollRef}
      style={{ gap: `${gap}px` }}
    >
      {withInitialAnimation && HeaderComponent ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 4, ease: "easeInOut", delay: 0.5 }}
          className={styles.headerTextImageContainer}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1.0 }}
            transition={{ duration: 6, ease: "easeInOut", delay: 0.5 }}
            style={{ maxWidth: "100%" }}
          >
            <HeaderComponent />
          </motion.div>
        </motion.div>
      ) : HeaderComponent ? (
        <HeaderComponent />
      ) : null}

      {groupedSections.map((group, groupIndex) => {
        const isReverse = group.some((section) => section.layout === "reverse");

        return (
          <div
            key={groupIndex}
            className={classNames(
              styles.contentContainer,
              isReverse && styles.contentContainerReverse
            )}
          >
            {group.map((section) => (
              <AnimatedSection
                key={section.id}
                section={section}
                styles={styles}
                userHasScrolled={
                  userHasScrolled || !requireScrollToShowComponents
                }
                constants={constants}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};
